import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const DIALOG_CLOSE_ANIMATION_TIMEOUT_MS = 400;

/**
 * Clears all common page scroll locks (theme + third-party apps like GoKwik).
 * @param {number} [scrollY]
 */
export function forceUnlockPageScroll(scrollY) {
  const body = document.body;
  const html = document.documentElement;

  let restoreY = typeof scrollY === 'number' ? scrollY : 0;
  if (!restoreY && body.style.top) {
    restoreY = Math.abs(parseInt(body.style.top, 10)) || 0;
  }

  body.style.removeProperty('overflow');
  body.style.removeProperty('overflow-x');
  body.style.removeProperty('overflow-y');
  body.style.removeProperty('position');
  body.style.removeProperty('top');
  body.style.removeProperty('left');
  body.style.removeProperty('width');
  body.style.removeProperty('height');
  body.style.removeProperty('padding-right');
  body.classList.remove('overflow-hidden', 'no-scroll', 'scroll-lock', 'gk-overflow-hidden');

  html.style.removeProperty('overflow');
  html.style.removeProperty('overflow-x');
  html.style.removeProperty('overflow-y');
  html.removeAttribute('scroll-lock');
  html.classList.remove('overflow-hidden', 'no-scroll', 'scroll-lock');

  if (restoreY) {
    window.scrollTo({ top: restoreY, behavior: 'instant' });
  }

  window.forceUnlockPageScroll = forceUnlockPageScroll;
}

// Expose for GoKwik / cart apps that bypass DialogComponent.close()
window.forceUnlockPageScroll = forceUnlockPageScroll;

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    this.refs.dialog?.addEventListener('close', this.#onDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.refs.dialog?.removeEventListener('close', this.#onDialogClose);

    // If this dialog locked the page and was removed while open, unlock.
    if (document.body.style.position === 'fixed' && !document.querySelector('dialog[open]')) {
      forceUnlockPageScroll(this.#previousScrollY);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Restores body scroll after dialog lock (position: fixed).
   */
  #unlockBodyScroll() {
    forceUnlockPageScroll(this.#previousScrollY);
  }

  /**
   * Safety net: unlock scroll when dialog closes by any path
   * (e.g. external dialog.close() from GoKwik that bypasses closeDialog).
   */
  #onDialogClose = () => {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog?.classList.remove('dialog-closing');

    const top = document.body.style.top;
    if (top) {
      this.#previousScrollY = Math.abs(parseInt(top, 10)) || this.#previousScrollY;
    }
    this.#unlockBodyScroll();
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      this.#previousScrollY = scrollY;

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      setTimeout(()=>dialog.showModal(),0)
      requestIdleCallback(() => this.dispatchEvent(new DialogOpenEvent()));

      setTimeout(()=>{
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      },0);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    setTimeout(()=>{
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    },0);

    setTimeout(()=>{
      dialog.close();
      dialog.classList.remove('dialog-closing');
    },0);

    setTimeout(()=>{
      this.dispatchEvent(new DialogCloseEvent());
    },0);
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
